// Domain Extractor — the Others/domain-extractor script as an admin page.
//
// Upload a CSV of companies, pick the website and/or email column, and get the
// same CSV back with columns appended (names from the original script):
//   domain      — the company domain used (website, else the email's domain)
//   ESP_company — mail provider of that domain (MX record)
//   ESP_email   — mail provider of the email's domain (MX record)
//   cloud       — who hosts that domain's website (DNS -> WHOIS on the IP)
// Personal mailbox domains (gmail.com, outlook.com...) are labelled, not
// looked up. A quick lookup box answers the same for one domain or email.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import ops from "../../styles/opsConsole.module.css";
import styles from "./DomainExtractor.module.css";
import { de, downloadUrl, ACTIVE, STATUS, readCsvHeaders, guessColumn, when, num, toneOf } from "./api";

function StatusBadge({ status }) {
  const s = STATUS[status] || { label: status, tone: "muted" };
  return (
    <span className={`${ops.badge} ${ops[s.tone]}`}>
      <span className={ops.dot} />
      {s.label}
    </span>
  );
}

function Pill({ value }) {
  const tone = toneOf(value);
  if (!tone) return <span className={styles.blank}>—</span>;
  return (
    <span className={`${styles.pill} ${styles[tone]}`} title={value}>
      <span className={styles.pillDot} />
      {value}
    </span>
  );
}

// 1 … 4 5 [6] 7 8 … 20
function pageList(page, pages) {
  const want = new Set([1, pages, page - 1, page, page + 1]);
  if (page <= 3) [2, 3, 4].forEach((p) => want.add(p));
  if (page >= pages - 2) [pages - 1, pages - 2, pages - 3].forEach((p) => want.add(p));
  const list = [...want].filter((p) => p >= 1 && p <= pages).sort((a, b) => a - b);
  const out = [];
  list.forEach((p, i) => {
    if (i && p - list[i - 1] > 1) out.push(`gap${p}`);
    out.push(p);
  });
  return out;
}

function Pager({ page, pages, matched, pageSize, onPage, onPageSize, sizes = [25, 50, 100], noun = "rows" }) {
  const from = matched ? (page - 1) * pageSize + 1 : 0;
  const to = Math.min(page * pageSize, matched);
  return (
    <div className={styles.pager}>
      <span className={styles.pagerInfo}>
        Showing <b>{num(from)}</b>–<b>{num(to)}</b> of <b>{num(matched)}</b> {noun}
      </span>
      <div className={styles.pagerBtns}>
        <button type="button" className={styles.pageBtn} disabled={page <= 1} onClick={() => onPage(1)} aria-label="First page">
          «
        </button>
        <button type="button" className={styles.pageBtn} disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous page">
          ‹
        </button>
        {pageList(page, pages).map((p) =>
          typeof p === "string" ? (
            <span key={p} className={styles.pageGap}>
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              className={`${styles.pageBtn} ${p === page ? styles.pageBtnOn : ""}`}
              onClick={() => onPage(p)}
              aria-current={p === page ? "page" : undefined}
            >
              {p}
            </button>
          ),
        )}
        <button type="button" className={styles.pageBtn} disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label="Next page">
          ›
        </button>
        <button type="button" className={styles.pageBtn} disabled={page >= pages} onClick={() => onPage(pages)} aria-label="Last page">
          »
        </button>
      </div>
      {onPageSize && (
        <label className={styles.toggle}>
          Per page
          <select className={styles.pageSize} value={pageSize} onChange={(e) => onPageSize(Number(e.target.value))}>
            {sizes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}

function QuickLookup() {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const run = async (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      setResult(await de("/lookup", { method: "POST", body: { value } }));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${ops.card} ${ops.cardPad}`}>
      <div className={styles.cardTitle}>Quick lookup</div>
      <span className={ops.secondary}>One website, domain or email address. WHOIS can take a few seconds.</span>
      <form className={styles.lookupRow} style={{ marginTop: 12 }} onSubmit={run}>
        <input
          className={styles.input}
          placeholder="acme.com, https://www.acme.com or jane@acme.com"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          aria-label="Website, domain or email"
        />
        <button type="submit" className={ops.btnPrimary} disabled={busy || !value.trim()}>
          {busy ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
          Look up
        </button>
      </form>
      {error && <div className={styles.error}>{error}</div>}
      {result?.personal && (
        <p className={ops.secondary} style={{ marginTop: 12 }}>
          {result.domain} is a personal mailbox provider, so it says nothing about a company. Not looked up.
        </p>
      )}
      {result && (
        <div className={styles.result}>
          <div className={styles.resultCell}>
            <div className={styles.resultLabel}>Domain</div>
            <div className={styles.resultValue}>{result.domain}</div>
          </div>
          <div className={styles.resultCell}>
            <div className={styles.resultLabel}>Mail provider (ESP)</div>
            <div className={styles.resultValue}>
              <Pill value={result.esp} />
            </div>
            <div className={styles.resultSub}>{result.mx || "no MX record"}</div>
          </div>
          <div className={styles.resultCell}>
            <div className={styles.resultLabel}>Website host (cloud)</div>
            <div className={styles.resultValue}>
              <Pill value={result.cloud} />
            </div>
            <div className={styles.resultSub}>{result.ip || "did not resolve"}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function NewJob({ defaults, maxRows, onStarted }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [websiteColumn, setWebsiteColumn] = useState("");
  const [emailColumn, setEmailColumn] = useState("");
  const [out, setOut] = useState(defaults);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => setOut(defaults), [defaults]);

  const pick = async (f) => {
    setError("");
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) {
      setError("Choose a .csv file. In Excel or Sheets: File → Download / Save as → CSV.");
      return;
    }
    try {
      const h = await readCsvHeaders(f);
      if (!h.length) throw new Error("The file has no header row");
      setFile(f);
      setHeaders(h);
      setWebsiteColumn(guessColumn(h, /^website$/i) || guessColumn(h, /website|domain|url|site/i));
      setEmailColumn(guessColumn(h, /^email$/i) || guessColumn(h, /e-?mail/i));
    } catch (err) {
      setError(err.message);
    }
  };

  const reset = () => {
    setFile(null);
    setHeaders([]);
    setWebsiteColumn("");
    setEmailColumn("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const start = async () => {
    setBusy(true);
    setError("");
    try {
      const form = new FormData();
      form.append("websiteColumn", websiteColumn);
      form.append("emailColumn", emailColumn);
      Object.entries(out).forEach(([k, v]) => form.append(`out_${k}`, v));
      form.append("file", file);
      const job = await de("/jobs", { method: "POST", form });
      toast.success("Job started");
      reset();
      onStarted(job);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const ready = file && (websiteColumn || emailColumn);

  return (
    <div className={`${ops.card} ${ops.cardPad}`}>
      <div className={styles.cardTitle}>Enrich a CSV</div>
      <span className={ops.secondary}>
        Up to {num(maxRows)} rows, 10 MB. Every original column is kept; the new columns are added at the end.
      </span>

      <div
        className={`${styles.drop} ${over ? styles.dropOver : ""}`}
        style={{ marginTop: 12 }}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          pick(e.dataTransfer.files?.[0]);
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          className={styles.hiddenInput}
          onChange={(e) => pick(e.target.files?.[0])}
        />
        <button type="button" className={ops.btn} onClick={() => inputRef.current?.click()}>
          {file ? "Choose another file" : "Choose CSV"}
        </button>
        {file ? (
          <span>
            <span className={styles.fileName}>{file.name}</span> · {headers.length} columns
          </span>
        ) : (
          <span>or drop a .csv file here</span>
        )}
      </div>

      {file && (
        <>
          <div className={styles.form}>
            <label className={styles.field}>
              Website column
              <select className={styles.select} value={websiteColumn} onChange={(e) => setWebsiteColumn(e.target.value)}>
                <option value="">— none —</option>
                {headers.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
              <span className={styles.hint}>
                Optional. Fills {out.domain}, {out.espCompany} and {out.cloud}; rows with no website use the email's
                domain.
              </span>
            </label>
            <label className={styles.field}>
              Email column
              <select className={styles.select} value={emailColumn} onChange={(e) => setEmailColumn(e.target.value)}>
                <option value="">— none —</option>
                {headers.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
              <span className={styles.hint}>
                Fills {out.espEmail}, and stands in for a missing website. Gmail, Outlook, Yahoo and other personal
                addresses are marked "Personal email", not looked up.
              </span>
            </label>
          </div>

          <details className={styles.details}>
            <summary className={styles.detailsSummary}>Output column names</summary>
            <div className={styles.form}>
              {[
                ["domain", "Company domain used"],
                ["espCompany", "Company mail provider"],
                ["espEmail", "Email mail provider"],
                ["cloud", "Website host"],
              ].map(([k, label]) => (
                <label key={k} className={styles.field}>
                  {label}
                  <input
                    className={styles.input}
                    value={out[k] || ""}
                    onChange={(e) => setOut({ ...out, [k]: e.target.value })}
                  />
                </label>
              ))}
            </div>
          </details>

          <div className={ops.actions} style={{ marginTop: 14 }}>
            <button type="button" className={ops.btnPrimary} disabled={!ready || busy} onClick={start}>
              {busy ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
              Start extraction
            </button>
            <button type="button" className={ops.btn} onClick={reset} disabled={busy}>
              Clear
            </button>
            {!websiteColumn && !emailColumn && <span className={ops.secondary}>Pick at least one column.</span>}
          </div>
        </>
      )}
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
}

// Per-column counts; each value is a button that filters the table to it.
function Summary({ job, filter, onFilter }) {
  const total = job.outputRows || 1;
  return (
    <div className={styles.summary}>
      {Object.entries(job.summary || {}).map(([col, counts]) => {
        const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
        return (
          <div key={col} className={styles.summaryCol}>
            <div className={styles.summaryTitle}>{col}</div>
            {entries.slice(0, 8).map(([k, v]) => {
              const on = filter?.column === col && filter?.value === k;
              return (
                <div key={k}>
                  <button
                    type="button"
                    className={`${styles.summaryBtn} ${on ? styles.summaryBtnOn : ""}`}
                    onClick={() => onFilter(on ? null : { column: col, value: k })}
                    title={on ? "Clear filter" : `Show only rows where ${col} is ${k}`}
                  >
                    {k === "(blank)" ? <span className={styles.blank}>(blank)</span> : <Pill value={k} />}
                    <span className={styles.summaryCount}>
                      {num(v)} <span className={ops.secondary} style={{ display: "inline" }}>{Math.round((v / total) * 100)}%</span>
                    </span>
                  </button>
                  <div className={styles.bar}>
                    <div className={styles.barFill} style={{ width: `${(v / total) * 100}%` }} />
                  </div>
                </div>
              );
            })}
            {entries.length > 8 && (
              <span className={ops.secondary}>+{entries.length - 8} more — use search to find them</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ResultsTable({ job, filter, onFilter }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Debounce typing so every keystroke isn't a request.
  useEffect(() => {
    const t = setTimeout(() => setDq(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  // A new query, filter or page size starts again from page 1.
  useEffect(() => setPage(1), [dq, filter, pageSize, job.id]);

  useEffect(() => {
    let live = true;
    const params = new URLSearchParams({ page, pageSize });
    if (dq) params.set("q", dq);
    if (filter) {
      params.set("column", filter.column);
      params.set("value", filter.value);
    }
    setLoading(true);
    de(`/jobs/${job.id}/rows?${params}`)
      .then((d) => {
        if (!live) return;
        setData(d);
        setError("");
      })
      .catch((err) => live && setError(err.message))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [job.id, job.finishedAt, page, pageSize, dq, filter]);

  const added = useMemo(() => new Set(job.addedColumns || []), [job.addedColumns]);
  const mono = useMemo(
    () => new Set([job.websiteColumn, job.emailColumn, job.outputColumns?.domain].filter(Boolean)),
    [job.websiteColumn, job.emailColumn, job.outputColumns],
  );
  const allCols = data?.columns || [];
  const keyCols = allCols.filter((c, i) => i === 0 || added.has(c) || c === job.websiteColumn || c === job.emailColumn);
  const cols = showAll ? allCols : keyCols;
  const pillCol = (c) => added.has(c) && c !== job.outputColumns?.domain;

  return (
    <>
      <div className={styles.toolbar}>
        <input
          className={styles.input}
          placeholder="Search any column…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search results"
        />
        {filter && (
          <span className={styles.filterTag}>
            {filter.column} = {filter.value}
            <button type="button" className={styles.filterTagX} onClick={() => onFilter(null)} aria-label="Clear filter">
              ×
            </button>
          </span>
        )}
        {loading && <CircularProgress size={14} />}
        <span className={styles.toolbarSpacer} />
        {allCols.length > keyCols.length && (
          <label className={styles.toggle}>
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
            All {allCols.length} columns
          </label>
        )}
      </div>

      {error && (
        <div className={ops.cardPad}>
          <div className={styles.error}>{error}</div>
        </div>
      )}

      {data && (
        <>
          {data.rows.length ? (
            <div className={styles.dataWrap}>
              <table className={styles.data}>
                <thead>
                  <tr>
                    <th className={`${styles.th} ${styles.thNum}`}>#</th>
                    {cols.map((c) => (
                      <th key={c} className={`${styles.th} ${added.has(c) ? styles.thAdded : ""}`}>
                        {c}
                        {added.has(c) && <span className={styles.thMark} title="Added by the extractor">ƒ</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={r._n} className={`${styles.tr} ${i % 2 ? styles.trOdd : ""}`}>
                      <td className={`${styles.td} ${styles.tdNum}`}>{r._n}</td>
                      {cols.map((c) => (
                        <td
                          key={c}
                          title={r[c] || ""}
                          className={`${styles.td} ${added.has(c) ? styles.tdAdded : ""} ${mono.has(c) ? styles.tdMono : ""}`}
                        >
                          {pillCol(c) ? <Pill value={r[c]} /> : r[c] || <span className={styles.blank}>—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.empty}>No rows match{dq ? ` "${dq}"` : ""}{filter ? ` with ${filter.column} = ${filter.value}` : ""}.</div>
          )}
          <Pager
            page={data.page}
            pages={data.pages}
            matched={data.matched}
            pageSize={data.pageSize}
            onPage={setPage}
            onPageSize={setPageSize}
            noun={data.matched === data.total ? "rows" : `matching rows (${num(data.total)} in file)`}
          />
        </>
      )}
    </>
  );
}

function JobPanel({ job, onCancel, onClose }) {
  const [filter, setFilter] = useState(null);
  useEffect(() => setFilter(null), [job.id]);

  const active = ACTIVE.includes(job.status);
  const pct = job.total ? Math.round((job.processed / job.total) * 100) : 0;
  const finished = (job.status === "done" || job.status === "cancelled") && job.outputRows != null;

  return (
    <div className={ops.card}>
      <h3>
        {job.fileName}
        <StatusBadge status={job.status} />
        <span style={{ flex: 1 }} />
        {finished && (
          <a className={ops.btnPrimary} href={downloadUrl(job.id)} style={{ textDecoration: "none", height: 32 }}>
            Download CSV
          </a>
        )}
        <button type="button" className={ops.link} onClick={onClose}>
          Close
        </button>
      </h3>
      <div className={ops.cardPad}>
        <span className={ops.secondary}>
          {[job.websiteColumn && `Website: ${job.websiteColumn}`, job.emailColumn && `Email: ${job.emailColumn}`]
            .filter(Boolean)
            .join(" · ")}{" "}
          · started {when(job.createdAt)} by {job.createdBy || "—"}
          {finished ? ` · ${num(job.outputRows)} rows` : ""}
        </span>

        {active && (
          <>
            <div className={styles.progress}>
              <div className={styles.progressBar} style={{ width: `${job.status === "queued" ? 0 : pct}%` }} />
            </div>
            <div className={styles.progressMeta}>
              <span>
                {job.status === "queued"
                  ? "Waiting for the job ahead of it to finish"
                  : job.total == null
                    ? "Reading the file…"
                    : `${num(job.processed)} of ${num(job.total)} rows`}
              </span>
              <span>{job.total ? `${pct}%` : ""}</span>
            </div>
            <div className={ops.actions} style={{ marginTop: 12 }}>
              <button type="button" className={ops.btnDanger} onClick={() => onCancel(job.id)}>
                Cancel
              </button>
            </div>
          </>
        )}

        {job.error && <div className={styles.error}>{job.error}</div>}

        {finished && job.status === "cancelled" && (
          <p className={ops.secondary} style={{ marginTop: 10 }}>
            Cancelled after {num(job.outputRows)} rows; the results and the download hold those rows.
          </p>
        )}
        {finished && <Summary job={job} filter={filter} onFilter={setFilter} />}
      </div>

      {finished && job.outputRows > 0 && <ResultsTable job={job} filter={filter} onFilter={setFilter} />}
    </div>
  );
}

const HISTORY_PAGE = 10;

function History({ jobs, selectedId, onOpen, onDelete }) {
  const [page, setPage] = useState(1);
  const pages = Math.max(1, Math.ceil(jobs.length / HISTORY_PAGE));
  useEffect(() => {
    if (page > pages) setPage(pages);
  }, [page, pages]);
  const shown = jobs.slice((page - 1) * HISTORY_PAGE, page * HISTORY_PAGE);

  return (
    <div className={ops.card}>
      <h3>
        History <span className={ops.count}>{jobs.length}</span>
      </h3>
      {!jobs.length ? (
        <div className={styles.empty}>No extractions yet. Upload a CSV above to start one.</div>
      ) : (
        <>
          <div className={styles.dataWrap} style={{ maxHeight: "none", borderTop: "none" }}>
            <table className={styles.data}>
              <thead>
                <tr>
                  <th className={styles.th}>File</th>
                  <th className={styles.th}>Status</th>
                  <th className={styles.th} style={{ textAlign: "right" }}>
                    Rows
                  </th>
                  <th className={styles.th}>Started by</th>
                  <th className={styles.th}>Started</th>
                  <th className={styles.th} />
                </tr>
              </thead>
              <tbody>
                {shown.map((j, i) => (
                  <tr key={j.id} className={`${styles.tr} ${i % 2 ? styles.trOdd : ""} ${j.id === selectedId ? styles.selectedRow : ""}`}>
                    <td className={styles.td}>
                      <span className={ops.primary}>{j.fileName}</span>
                      <span className={ops.secondary}>{[j.websiteColumn, j.emailColumn].filter(Boolean).join(" · ")}</span>
                    </td>
                    <td className={styles.td}>
                      <StatusBadge status={j.status} />
                    </td>
                    <td className={styles.td} style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {ACTIVE.includes(j.status) && j.total ? `${num(j.processed)} / ${num(j.total)}` : num(j.outputRows ?? j.total)}
                    </td>
                    <td className={styles.td}>{j.createdBy || "—"}</td>
                    <td className={styles.td}>{when(j.createdAt)}</td>
                    <td className={styles.td}>
                      <div className={ops.actions} style={{ justifyContent: "flex-end" }}>
                        <button type="button" className={ops.btn} onClick={() => onOpen(j.id)}>
                          View
                        </button>
                        {j.outputRows != null && (
                          <a className={ops.btn} href={downloadUrl(j.id)} style={{ textDecoration: "none" }}>
                            Download
                          </a>
                        )}
                        {!ACTIVE.includes(j.status) && (
                          <button type="button" className={ops.btnDanger} onClick={() => onDelete(j)}>
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {jobs.length > HISTORY_PAGE && (
            <Pager page={page} pages={pages} matched={jobs.length} pageSize={HISTORY_PAGE} onPage={setPage} noun="jobs" />
          )}
        </>
      )}
    </div>
  );
}

const DomainExtractor = () => {
  const [state, setState] = useState(null); // { jobs, maxRows, defaults }
  const [selected, setSelected] = useState(null); // full job

  const refresh = useCallback(async () => {
    try {
      setState(await de("/jobs"));
    } catch (err) {
      toast.error(err.message);
    }
  }, []);

  const open = useCallback(async (id) => {
    try {
      setSelected(await de(`/jobs/${id}`));
    } catch (err) {
      toast.error(err.message);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Poll the open job while it runs; refresh the history when it settles.
  const selectedId = selected?.id;
  const selectedActive = selected && ACTIVE.includes(selected.status);
  useEffect(() => {
    if (!selectedActive) return undefined;
    const t = setInterval(async () => {
      try {
        const job = await de(`/jobs/${selectedId}`);
        setSelected(job);
        if (!ACTIVE.includes(job.status)) refresh();
      } catch {
        /* next tick retries */
      }
    }, 1500);
    return () => clearInterval(t);
  }, [selectedId, selectedActive, refresh]);

  // Keep history progress honest for jobs that are not open.
  const anyActive = state?.jobs?.some((j) => ACTIVE.includes(j.status));
  useEffect(() => {
    if (!anyActive) return undefined;
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [anyActive, refresh]);

  const cancel = async (id) => {
    try {
      setSelected(await de(`/jobs/${id}/cancel`, { method: "POST" }));
      refresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (job) => {
    if (!window.confirm(`Delete "${job.fileName}" and its results?`)) return;
    try {
      await de(`/jobs/${job.id}`, { method: "DELETE" });
      if (selectedId === job.id) setSelected(null);
      refresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className={`${ops.root} ${styles.page}`}>
      <div className={ops.head}>
        <div>
          <h1 className={styles.title}>
            Domain <span className={styles.titleAccent}>Extractor</span>
          </h1>
          <p className={ops.sub}>
            Find who runs a company's email and who hosts its website. Mail provider comes from the domain's MX record
            (Google, Outlook or Other); website host comes from WHOIS on the site's IP (AWS, GCP, Azure, Cloudflare, or
            the network owner's name). No website? The email's domain is used instead. Personal addresses (Gmail,
            Outlook, Yahoo…) are marked "Personal email".
          </p>
        </div>
      </div>

      {!state ? (
        <div className={ops.centre}>
          <CircularProgress size={24} />
        </div>
      ) : (
        <>
          <QuickLookup />
          <NewJob
            defaults={state.defaults}
            maxRows={state.maxRows}
            onStarted={(job) => {
              setSelected(job);
              refresh();
            }}
          />
          {selected && <JobPanel job={selected} onCancel={cancel} onClose={() => setSelected(null)} />}
          <History jobs={state.jobs} selectedId={selectedId} onOpen={open} onDelete={remove} />
        </>
      )}
    </div>
  );
};

export default DomainExtractor;
